import { Column, ITerm, INode, IHandleAddMatchesParams, IHandleGetMatchesParams, ISelectedMatch, IHandleDeleteMatchesParams, IRemoveMatchData, IHandleUpdateMatchFactorsParams } from './Types/BulkPricingTypes';
import { formatCurrency } from '../../../ps-components/Utils/currencyUtils';
import { getMatchData, addMatches, getOrgJobsData, deleteBulkMatches, updateMatches } from './BulkPricingAPIs';
import { errorMap } from './constants';

export function getFieldData(element: string, row: any[], terms: ITerm[]) {
  const index = terms?.findIndex((item: ITerm) => item.term === element);
  return row[index]
};

export const formatValue = (column: Column, data: (string | number | null)[], terms: any[]) => {
  const currency = getFieldData('currency_default', data, terms);
  if (column.dataType === 'monetary') return formatCurrency(getFieldData(column.term, data, terms), currency || undefined) || '-';
  else return getFieldData(column.term, data, terms) || '-';
}

/** loop through match data and group datacuts belonging to the same survey job */
type DataCutList = (string | number)[][] | (ISelectedMatch | INode)[];
export const groupDataCutsBySurveyJob = (data: DataCutList = [], terms: ITerm[]): INode[] => {

  const surveyJobs = data.reduce((acc: INode[], item: (string | number)[] | ISelectedMatch | INode) => {

    const dataCut = (Array.isArray(item)) ? 
      item as (string | number)[] : 
      item.hasOwnProperty('node') ?
      (item as ISelectedMatch).node.data :
      (item as INode).data;
    const surveyJobCode: string = getFieldData('job_code_default', dataCut, terms);
    const surveyCode: string = getFieldData('survey_code_default', dataCut, terms);
    const cutId: number = getFieldData('cut_id_default', dataCut, terms);
    const surveyJobID = `${surveyCode}_${surveyJobCode}`;
    const surveyJob = acc.find((job) => job.id === surveyJobID);
    if (surveyJob) {
      surveyJob.children.push({ id: cutId, type: 'DATA_CUT', children: [], data: dataCut, terms });
    } else {
      acc.push({
        id: surveyJobID,
        type: 'SURVEY_JOB',
        children: [{ id: cutId, type: 'DATA_CUT', children: [], data: dataCut, terms }],
        data: [],
        terms: terms
      });
    }

    return acc;
  }, []);

  return surveyJobs;
}

export const formatDataCuts = (dataCuts: any[], terms: ITerm[]): INode[] => {
  return dataCuts.map((dataCut: any[]) => {
    return {
      id: getFieldData('cut_id_default', dataCut, terms),
      type: 'DATA_CUT',
      children: [],
      data: dataCut,
      terms
    }
  })
};

/** takes in match data and returns an org job node with survey job nodes as children */
export const formatMatchesData = (matchesData: any, job: INode): INode => {
  const orgJob = {
    id: job.id,
    type: 'ORG_JOB',
    children: [],
    data: job.data,
    terms: job.terms
  } as INode;
  const surveyJobs = groupDataCutsBySurveyJob(matchesData.data, matchesData.terms);
  orgJob.children.push(...surveyJobs);
  return orgJob;
};

/** given a list of match errors, create a string with the survey job title and data cut name for each failed match */
export const findDataCutInfo = (errorList: { addMatchError: string, cut_id: number }[], job: INode, errorType: string) => {
  const filteredList = errorList.filter((error) => {
    return error.addMatchError === errorType;
  });

  /** map a new array with the survey job title and data cut titles for each failed match */
  const updatedList = filteredList.map((error) => {

    /** find the survey job that the data cut belongs to */
    const surveyJob = job.children.find((surveyJob: INode) => {
      return surveyJob.children.some((dataCut: INode) => getFieldData('cut_id_default', dataCut.data, dataCut.terms) === error.cut_id);
    });
    
    /** identify the data cut with the matching cut ID */
    const dataCut = surveyJob?.children.find((dataCut: INode) => getFieldData('cut_id_default', dataCut.data, dataCut.terms) === error.cut_id);
    const surveyCode = getFieldData('survey_code_default', dataCut?.data as (string | number)[], dataCut?.terms as any[]);
    const jobCode = getFieldData('job_code_default', dataCut?.data as (string | number)[], dataCut?.terms as any[]);
    const jobTitle = getFieldData('job_title_default', dataCut?.data as (string | number)[], dataCut?.terms as any[]);
    const jobScope = getFieldData('job_scope_default', dataCut?.data as (string | number)[], dataCut?.terms as any[]);

    return (
      `* ${surveyCode}, ${jobCode}, ${jobTitle}, ${jobScope}`
    );
  });
  
  return updatedList;
}

/** add matches to an org job, update the org job in state, and handle alerts */
export const handleAddMatches = async (
  { 
    setMessages,
    setHasDisplayedMessages,
    updateMatchesInState,
    job,
    cutIDs, 
    benchmarkYear, 
    agingCode,
    currency,
    masterAccountID,
    setMatchDataLoading,
    displayIn
  }: IHandleAddMatchesParams
): Promise<void> => {
  const jobTitle: string = getFieldData('org_job_title_default', job.data, job.terms) || 'your job';
  const orgJobID: number = getFieldData('org_job_id_default', job.data, job.terms);

  setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = true;
    return newMatchDataLoading;
  });

  const results: any = await addMatches({ orgJobID, cutIDs, benchmarkYear, agingCode });

  const [orgJobData, updatedJob] = await Promise.all([
    getOrgJobsData({ jobs: [{ jobID: job.id }], masterAccountID}),
    handleGetMatches({
      masterAccountID,
      benchmarkYear,
      currency,
      job,
      updateMatchesInState,
      jobStateStale: false,
      setMatchDataLoading,
      manageLoadingState: false,
      displayIn
    })
  ]);

  const newJob = orgJobData.data[0] ? formatOrgJob(orgJobData.data[0], orgJobData.terms) : job;
  newJob.children = updatedJob.children;
  updateMatchesInState(newJob);
  setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = false;
    return newMatchDataLoading;
  });

  setMessages([]);
  const messageTypes = new Set<string>(results.data.addMatchErrorList.map((error: { addMatchError: string, cut_id: number }) => error.addMatchError));
  const errorList: { addMatchError: string, cut_id: number }[] = results.data.addMatchErrorList;
  const newMessages: { text: string | string[], type: string }[] = [];
  messageTypes.forEach((message) => {
    const formattedList = findDataCutInfo(errorList, updatedJob, message);
      const updatedErrorMessage = errorMap.get(message) || message;
      newMessages.push({text: [`The following matches were not added to ${jobTitle} because ${updatedErrorMessage}: `, ...formattedList], type: 'warning' });
  });
  if (results.data.newMatchIdList.length) {
    newMessages.push({ text: `${results.data.newMatchIdList.length} ${results.data.newMatchIdList.length === 1 ? 'match' : 'matches'} successfully added to ${jobTitle}`, type: 'success' });
  }
  setMessages(newMessages);
  setHasDisplayedMessages(false);
};

/** get match data, update org job state, and manage loading state */
export const handleGetMatches = async (
  { 
    masterAccountID, 
    benchmarkYear, 
    currency, 
    job,
    updateMatchesInState,
    jobStateStale,
    setMatchDataLoading,
    manageLoadingState = true,
    displayIn
 }: IHandleGetMatchesParams
) => {
  const jobCode: string = getFieldData('org_job_code_default', job.data, job.terms);
  const jobCodeKey: string = getFieldData('org_job_code_key_default', job.data, job.terms);

  if (manageLoadingState) setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = true;
    return newMatchDataLoading;
  });

  const matchesData = await getMatchData({ masterAccountID, benchmarkYear, currency, jobCode, jobCodeKey, displayIn });
  const updatedJob = formatMatchesData(matchesData, job);
  if (jobStateStale) updateMatchesInState(updatedJob);

  if (manageLoadingState) setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = false;
    return newMatchDataLoading;
  });

  return updatedJob;
}

export const formatOrgJob = (orgJobRaw: any, terms: any[]): INode => {
  const orgJob = {
    id: getFieldData('org_job_id_default', orgJobRaw, terms),
    type: 'ORG_JOB',
    children: [],
    data: orgJobRaw,
    terms
  } as INode;

  return orgJob;
}

export const handleDeleteMatches = async (
  { 
    orgJobs, 
    selectedMatches, 
    benchmarkYear, 
    setMatchDataLoading, 
    setSelectedMatches, 
    updateMatchesInState, 
    masterAccountID,
    displayIn
  }: IHandleDeleteMatchesParams
) => {
  const removeMatchesData: IRemoveMatchData[] = [];
  const updateJobsData: INode[] = selectedMatches.reduce((acc: INode[], cut) => {
    const affiliatedJob = orgJobs.find(job => job.id === cut.jobId) as INode;
    const orgJobId = getFieldData('org_job_id_default', affiliatedJob.data, affiliatedJob.terms);
    const orgJobGroup = removeMatchesData.find(group => group.org_job_id === orgJobId);
    
    if (orgJobGroup) {
      orgJobGroup.remove_cut.push(getFieldData('cut_id_default', cut.node.data, cut.node.terms));
    } else {
      acc.push(affiliatedJob);
      removeMatchesData.push({
        org_job_id: orgJobId,
        remove_cut: [getFieldData('cut_id_default', cut.node.data, cut.node.terms)],
        benchmarkYear
      });
    }

    return acc;
  }, []);

  updateJobsData.forEach(job => {
    setMatchDataLoading((pending) => {
      const newMatchDataLoading = { ...pending };
      newMatchDataLoading[job.id] = true;
      return newMatchDataLoading;
    });
  });

  const result = await deleteBulkMatches(removeMatchesData);

  await Promise.all(updateJobsData.map(async (job) => {
    const [orgJobData, updatedJob] = await Promise.all([
      getOrgJobsData({ jobs: [{ jobID: job.id }], masterAccountID}),
      handleGetMatches({
        masterAccountID,
        benchmarkYear,
        currency: getFieldData('currency_default', job.data, job.terms),
        job,
        updateMatchesInState,
        jobStateStale: false,
        setMatchDataLoading,
        manageLoadingState: false,
        displayIn
      })
    ]);
  
    const newJob = orgJobData.data[0] ? formatOrgJob(orgJobData.data[0], orgJobData.terms) : job;
    newJob.children = updatedJob.children;
    updateMatchesInState(newJob);
  }));

  if (result.success) {
    setSelectedMatches([]);
  } else {
    console.error(`Failed to remove data`, result.reason || result.error);
  }
  updateJobsData.forEach(job => {
    setMatchDataLoading((pending) => {
      const newMatchDataLoading = { ...pending };
      newMatchDataLoading[job.id] = false;
      return newMatchDataLoading;
    });
  });
};

export const handleUpdateMatchFactors = async (
  { 
    orgJobId, 
    matches, 
    benchmarkYear, 
    editPastYears, 
    masterAccountID, 
    currency, 
    job, 
    updateMatchesInState, 
    setMatchDataLoading, 
    displayIn
  }: IHandleUpdateMatchFactorsParams
) => {
  setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = true;
    return newMatchDataLoading;
  });

  await updateMatches({ orgJobId, matches, benchmarkYear, editPastYears });

  const [orgJobData, updatedJob] = await Promise.all([
    getOrgJobsData({ jobs: [{ jobID: job.id }], masterAccountID}),
    handleGetMatches({
      masterAccountID,
      benchmarkYear,
      currency,
      job,
      updateMatchesInState,
      jobStateStale: false,
      setMatchDataLoading,
      manageLoadingState: false,
      displayIn
    })
  ]);

  const newJob = orgJobData.data[0] ? formatOrgJob(orgJobData.data[0], orgJobData.terms) : job;
  newJob.children = updatedJob.children;
  updateMatchesInState(newJob);

  setMatchDataLoading((pending) => {
    const newMatchDataLoading = { ...pending };
    newMatchDataLoading[job.id] = false;
    return newMatchDataLoading;
  });
}

export const standardizeDecimals = (num: number, fixedDecimal: number = 3) => Number.isInteger(num) ? num : num.toFixed(fixedDecimal);
